/**
 * Mock Authentication Utility for Development
 *
 * This utility allows developers to quickly switch between different tenant types
 * during development to test the routing and UI for different portals.
 *
 * Usage in browser console:
 * ```
 * setMockTenantType('CENTRAL_AUTHORITY')  // Test SENUM Operator Console
 * setMockTenantType('MEMBER_SELF_MANAGED') // Test Control Tower
 * setMockTenantType('MEMBER_HOSTED')       // Test Managed Portal
 * setMockTenantType('MEMBER_HOSTED_LIGHT') // Test Light Portal
 * getMockTenantType()                      // Get current tenant type
 * ```
 */

import { useXRoadStore } from '../store/xroad.store';
import { TenantType, HostingTier, type TenantMetadata, HostingTierLimits } from '../types/tenant.types';

// ============================================================================
// Mock Tenant Data
// ============================================================================

/**
 * Mock tenant metadata for each tenant type.
 * Used for development and testing.
 */
const MOCK_TENANTS: Record<TenantType, TenantMetadata> = {
  [TenantType.CENTRAL_AUTHORITY]: {
    tenantId: 'senum',
    tenantName: 'SENUM - Sénégal Numérique',
    tenantType: TenantType.CENTRAL_AUTHORITY,
    parentTenantId: null,
    hostingTier: null,
    maxServices: null,
    maxApiCallsPerMonth: null,
    xroadMemberIdentifier: null,
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
  },

  [TenantType.MEMBER_SELF_MANAGED]: {
    tenantId: 'min-finance',
    tenantName: 'Ministère des Finances et du Budget',
    tenantType: TenantType.MEMBER_SELF_MANAGED,
    parentTenantId: null,
    hostingTier: null,
    maxServices: null, // Unlimited
    maxApiCallsPerMonth: null, // Unlimited
    xroadMemberIdentifier: 'SN-GOV/GOV/1234fin',
    isActive: true,
    createdAt: '2024-02-15T00:00:00Z',
  },

  [TenantType.MEMBER_HOSTED]: {
    tenantId: 'min-education',
    tenantName: "Ministère de l'Éducation Nationale",
    tenantType: TenantType.MEMBER_HOSTED,
    parentTenantId: 'senum',
    hostingTier: HostingTier.STANDARD,
    maxServices: HostingTierLimits[HostingTier.STANDARD].maxServices,
    maxApiCallsPerMonth: HostingTierLimits[HostingTier.STANDARD].maxApiCallsPerMonth,
    xroadMemberIdentifier: 'SN-GOV/GOV/1234edu',
    isActive: true,
    createdAt: '2024-03-10T00:00:00Z',
  },

  [TenantType.MEMBER_HOSTED_LIGHT]: {
    tenantId: 'ngo-enda',
    tenantName: 'ENDA Tiers Monde',
    tenantType: TenantType.MEMBER_HOSTED_LIGHT,
    parentTenantId: 'senum',
    hostingTier: HostingTier.LIGHT,
    maxServices: HostingTierLimits[HostingTier.LIGHT].maxServices,
    maxApiCallsPerMonth: HostingTierLimits[HostingTier.LIGHT].maxApiCallsPerMonth,
    xroadMemberIdentifier: 'SN-GOV/ORG/enda',
    isActive: true,
    createdAt: '2024-04-05T00:00:00Z',
  },

  [TenantType.SUBSYSTEM_HOSTED]: {
    tenantId: 'mef-budget',
    tenantName: 'Direction du Budget',
    tenantType: TenantType.SUBSYSTEM_HOSTED,
    parentTenantId: 'min-finance',
    hostingTier: null,
    maxServices: null, // Shares parent limits
    maxApiCallsPerMonth: null,
    xroadMemberIdentifier: 'SN-GOV/GOV/1234fin',
    xroadSubsystemCode: 'BUDGET',
    isActive: true,
    createdAt: '2024-05-20T00:00:00Z',
  },

  [TenantType.SUBSYSTEM_INTERNAL]: {
    tenantId: 'mef-taxes',
    tenantName: 'Direction des Impôts',
    tenantType: TenantType.SUBSYSTEM_INTERNAL,
    parentTenantId: 'min-finance',
    hostingTier: null,
    maxServices: null,
    maxApiCallsPerMonth: null,
    xroadMemberIdentifier: 'SN-GOV/GOV/1234fin',
    xroadSubsystemCode: 'TAXES',
    isActive: true,
    createdAt: '2024-05-25T00:00:00Z',
  },
};

// ============================================================================
// Mock Authentication Functions
// ============================================================================

/**
 * Set mock tenant type for development.
 * This updates the Zustand store and triggers a re-render with the new portal.
 *
 * @param type - Tenant type to switch to
 * @returns Success message
 */
export function setMockTenantType(type: TenantType | string): string {
  // Validate tenant type
  const tenantType = type as TenantType;
  if (!Object.values(TenantType).includes(tenantType)) {
    console.error(
      `Invalid tenant type: "${type}". Must be one of:`,
      Object.values(TenantType)
    );
    return `Error: Invalid tenant type "${type}"`;
  }

  // Get mock tenant metadata
  const mockTenant = MOCK_TENANTS[tenantType];

  // Update Zustand store
  const store = useXRoadStore.getState();
  store.setTenantMetadata(mockTenant);

  // Log success
  const portalNames: Record<TenantType, string> = {
    [TenantType.CENTRAL_AUTHORITY]: 'Console Opérateur SENUM',
    [TenantType.MEMBER_SELF_MANAGED]: 'Tour de Contrôle',
    [TenantType.MEMBER_HOSTED]: 'Portail Hébergé',
    [TenantType.MEMBER_HOSTED_LIGHT]: 'Portail Light',
    [TenantType.SUBSYSTEM_HOSTED]: 'Sous-système Hébergé',
    [TenantType.SUBSYSTEM_INTERNAL]: 'Sous-système Interne',
  };

  const message = `✅ Switched to ${portalNames[tenantType]} (${mockTenant.tenantName})`;
  console.log(message);
  console.log('Tenant metadata:', mockTenant);

  return message;
}

/**
 * Get current mock tenant type.
 *
 * @returns Current tenant metadata
 */
export function getMockTenantType(): TenantMetadata | null {
  const store = useXRoadStore.getState();
  return store.tenantMetadata;
}

/**
 * Get all available mock tenant types.
 *
 * @returns Array of tenant types with descriptions
 */
export function listMockTenantTypes(): Array<{ type: TenantType; name: string; description: string }> {
  return [
    {
      type: TenantType.CENTRAL_AUTHORITY,
      name: 'SENUM - Sénégal Numérique',
      description: 'Console Opérateur SENUM - Full ecosystem visibility',
    },
    {
      type: TenantType.MEMBER_SELF_MANAGED,
      name: 'Ministère des Finances',
      description: 'Tour de Contrôle - Self-managed Security Server',
    },
    {
      type: TenantType.MEMBER_HOSTED,
      name: "Ministère de l'Éducation",
      description: 'Portail Hébergé - SENUM-managed Security Server (Standard)',
    },
    {
      type: TenantType.MEMBER_HOSTED_LIGHT,
      name: 'ENDA Tiers Monde',
      description: 'Portail Light - Shared Security Server (Light tier)',
    },
    {
      type: TenantType.SUBSYSTEM_HOSTED,
      name: 'Direction du Budget',
      description: 'Sous-système Hébergé - Autonomous subsystem',
    },
    {
      type: TenantType.SUBSYSTEM_INTERNAL,
      name: 'Direction des Impôts',
      description: 'Sous-système Interne - Internal subsystem',
    },
  ];
}

/**
 * Reset to default tenant type (CENTRAL_AUTHORITY).
 */
export function resetMockTenantType(): string {
  return setMockTenantType(TenantType.CENTRAL_AUTHORITY);
}

// ============================================================================
// Window API (for browser console)
// ============================================================================

// Expose functions to window for easy access in browser console
if (typeof window !== 'undefined') {
  (window as any).setMockTenantType = setMockTenantType;
  (window as any).getMockTenantType = getMockTenantType;
  (window as any).listMockTenantTypes = listMockTenantTypes;
  (window as any).resetMockTenantType = resetMockTenantType;

  // Log instructions on dev server start
  console.log(`
╔══════════════════════════════════════════════════════════════════════════╗
║  🔐 X-Road Mock Authentication Available                                 ║
╠══════════════════════════════════════════════════════════════════════════╣
║  Use these commands in the browser console to test different portals:    ║
║                                                                          ║
║  setMockTenantType('CENTRAL_AUTHORITY')  → SENUM Operator Console       ║
║  setMockTenantType('MEMBER_SELF_MANAGED') → Control Tower (Self-Mgd)    ║
║  setMockTenantType('MEMBER_HOSTED')       → Managed Portal (Hosted)     ║
║  setMockTenantType('MEMBER_HOSTED_LIGHT') → Light Portal (Light)        ║
║                                                                          ║
║  getMockTenantType()      → Show current tenant                         ║
║  listMockTenantTypes()    → List all available tenant types             ║
║  resetMockTenantType()    → Reset to SENUM                              ║
╚══════════════════════════════════════════════════════════════════════════╝
  `);
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if current tenant has a specific type.
 *
 * @param type - Tenant type to check
 * @returns True if current tenant matches the type
 */
export function isCurrentTenantType(type: TenantType): boolean {
  const currentTenant = getMockTenantType();
  return currentTenant?.tenantType === type;
}

/**
 * Check if current tenant is SENUM (Central Authority).
 */
export function isCurrentTenantSenum(): boolean {
  return isCurrentTenantType(TenantType.CENTRAL_AUTHORITY);
}

/**
 * Check if current tenant is self-managed.
 */
export function isCurrentTenantSelfManaged(): boolean {
  return isCurrentTenantType(TenantType.MEMBER_SELF_MANAGED);
}

/**
 * Check if current tenant is hosted.
 */
export function isCurrentTenantHosted(): boolean {
  return (
    isCurrentTenantType(TenantType.MEMBER_HOSTED) ||
    isCurrentTenantType(TenantType.MEMBER_HOSTED_LIGHT)
  );
}

/**
 * Check if current tenant is a subsystem.
 */
export function isCurrentTenantSubsystem(): boolean {
  return (
    isCurrentTenantType(TenantType.SUBSYSTEM_HOSTED) ||
    isCurrentTenantType(TenantType.SUBSYSTEM_INTERNAL)
  );
}
